import axios from 'axios';

const AIRTABLE_CONFIG = {
  baseId: process.env.REACT_APP_AIRTABLE_BASE_ID,
  apiKey: process.env.REACT_APP_AIRTABLE_API_KEY,
  tables: {
    blog: process.env.REACT_APP_AIRTABLE_BLOG_TABLE || 'Blogs',
    contact: process.env.REACT_APP_AIRTABLE_CONTACT_TABLE || 'ContactUs',
    members: process.env.REACT_APP_AIRTABLE_MEMBERS_TABLE || 'Members',
    services: process.env.REACT_APP_AIRTABLE_SERVICES_TABLE || 'Services'
  }
};

// Testimonials API
export const testimonialsAPI = {
  getAll: async () => {
    try {
      const table = process.env.REACT_APP_AIRTABLE_TESTIMONIALS_TABLE || 'Testimonials';

      const cacheKey = `testimonials_cache_${AIRTABLE_CONFIG.baseId}_${table}`;
      const cacheTTL = 1000 * 60 * 5; // 5 minutes
      try {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Date.now() - parsed.ts < cacheTTL) {
            return parsed.data;
          }
        }
      } catch (e) {}

      const allRecords = [];
      let offset = undefined;

      do {
        const params = {};
        if (offset) params.offset = offset;
        const response = await api.get(`/${table}`, { params });
        const pageRecords = response.data.records || [];
        allRecords.push(...pageRecords);
        offset = response.data.offset;
      } while (offset);

      const final = transformRecords(allRecords);
      try { sessionStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), data: final })); } catch (e) {}
      return final;
    } catch (err) {
      console.error('❌ Error fetching testimonials:', err.response?.data || err.message);
      throw new Error(`Failed to fetch testimonials: ${err.response?.data?.error?.message || err.message}`);
    }
  }
};



const api = axios.create({
  baseURL: `https://api.airtable.com/v0/${AIRTABLE_CONFIG.baseId}`,
  headers: {
    'Authorization': `Bearer ${AIRTABLE_CONFIG.apiKey}`,
    'Content-Type': 'application/json'
  },
  timeout: 10000
});

// Utility function to transform Airtable records
export const transformRecords = (records) => {
  if (!records) return [];
  return records.map(record => ({
    id: record.id,
    ...record.fields
  }));
};

// Blog Posts API
export const blogAPI = {
  getAll: async () => {
    try {
      const response = await api.get(`/${AIRTABLE_CONFIG.tables.blog}?sort[0][field]=CreatedAt&sort[0][direction]=desc`);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching blogs:', error.response?.data || error.message);
      throw new Error(`Failed to fetch blogs: ${error.response?.data?.error?.message || error.message}`);
    }
  }
};

// Contact Form API
export const contactAPI = {
  submit: async (formData) => {
    try {
      const response = await api.post(`/${AIRTABLE_CONFIG.tables.contact}`, {
        fields: {
          Name: formData.name,
          Email: formData.email,
          Message: formData.message,
          Type: formData.type,
          Submitted: new Date().toISOString(),
          Status: 'New'
        }
      });
      return response.data;
    } catch (error) {
      console.error('❌ Error submitting contact form:', error.response?.data || error.message);
      throw new Error(`Failed to submit form: ${error.response?.data?.error?.message || error.message}`);
    }
  }
};

// Members API (for services page - linked to Services table via People field)
export const contactsAPI = {
  getAll: async () => {
    try {
      const cacheKey = `members_cache_${AIRTABLE_CONFIG.baseId}_${AIRTABLE_CONFIG.tables.members}`;
      const cacheTTL = 1000 * 60 * 5; // 5 minutes
      try {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Date.now() - parsed.ts < cacheTTL) {
            return parsed.data;
          }
        }
      } catch (e) {}

      const allRecords = [];
      let offset = undefined;

      do {
        const params = {};
        if (offset) params.offset = offset;
        const response = await api.get(`/${AIRTABLE_CONFIG.tables.members}`, { params });
        const pageRecords = response.data.records || [];
        allRecords.push(...pageRecords);
        offset = response.data.offset;
      } while (offset);

      const final = transformRecords(allRecords);
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), data: final }));
      } catch (e) {}

      return final;
    } catch (error) {
      console.error('❌ Error fetching members:', error.response?.data || error.message);
      throw new Error(`Failed to fetch members: ${error.response?.data?.error?.message || error.message}`);
    }
  }
};

// Services API
export const servicesAPI = {
  getAll: async () => {
    try {
      // Simple sessionStorage caching to reduce Airtable calls during a session
      const cacheKey = `services_cache_${AIRTABLE_CONFIG.baseId}_${AIRTABLE_CONFIG.tables.services}`;
      const cacheTTL = 1000 * 60 * 5; // 5 minutes
      try {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Date.now() - parsed.ts < cacheTTL) {
            return parsed.data;
          }
        }
      } catch (e) {
        // ignore sessionStorage failures
      }

      // Helper to accumulate all pages of records using Airtable's offset
      const allRecords = [];
      let offset = undefined;
      // No sort by 'Order' because this table does not have an Order field
      const paramsBase = {};

      do {
        const params = { ...paramsBase };
        if (offset) params.offset = offset;

        const response = await api.get(`/${AIRTABLE_CONFIG.tables.services}`, { params });
        const pageRecords = response.data.records || [];
        allRecords.push(...pageRecords);
        offset = response.data.offset;
      } while (offset);

      const final = transformRecords(allRecords);
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), data: final }));
      } catch (e) {
        // ignore storage errors
      }

      return final;
    } catch (error) {
      console.error('❌ Error fetching services:', error.response?.data || error.message);
      throw new Error(`Failed to fetch services: ${error.response?.data?.error?.message || error.message}`);
    }
  }
};


// Test connection - FIXED VERSION
export const testConnection = async () => {
  try {
    // Make a simple request to check connection
    await api.get(`/${AIRTABLE_CONFIG.tables.blog}?maxRecords=1`);
    return true;
  } catch (error) {
    console.error('❌ Airtable connection failed:', error.response?.data || error.message);
    return false;
  }
};